import firebase from '@/firebase'
const functions = firebase.functions
const db = firebase.firestore()

const state = {
  modelReportSummaries: {},
  aggregatedSummaryVotes: {},
  detailedSummaryVotes: {}
}

const getters = {
  aggregatedSummaryVotes: state => {
    const aggregated = {}
    Object.entries(state.aggregatedSummaryVotes).forEach(([clientId, summaries]) => {
      if (!aggregated[clientId]) {
        aggregated[clientId] = {}
      }
      Object.entries(summaries).forEach(([summaryId, userVotes]) => {
        const stats = {
          totalVotes: Object.keys(userVotes).length,
          thumbsUp: Object.values(userVotes).filter(v => v.vote === 'up').length,
          thumbsDown: Object.values(userVotes).filter(v => v.vote === 'down').length,
          comments: Object.values(userVotes).filter(v => v.comment && v.comment.length > 0).length
        }
        aggregated[clientId][summaryId] = stats
      })
    })
    return aggregated
  },
  detailedSummaryVotes: state => state.detailedSummaryVotes
}

const actions = {
  async fetchDetailedSummaryVotes ({ commit }, clientId) {
    try {
      let query = db.collectionGroup('summaryVotes')
      if (clientId) {
        query = db.collection('ai_summaries').doc(clientId).collection('model_report').orderBy('timestamp', 'desc').limit(1)
      }

      const detailedVotes = {}
      const snapshot = clientId
        ? await query.get().then(snap => {
          if (snap.docs.length === 0) return []
          return snap.docs[0].ref.collection('summaryVotes').get()
        })
        : await query.get()
      for (const voteDoc of snapshot.docs) {
        const pathSegments = voteDoc.ref.path.split('/')
        const docClientId = clientId || pathSegments[1]
        const userId = voteDoc.id
        const userVotes = voteDoc.data()
        if (!detailedVotes[docClientId]) {
          detailedVotes[docClientId] = {}
        }

        Object.entries(userVotes).forEach(([summaryId, voteValue]) => {
          if (summaryId.endsWith('-comment')) return
          if (!detailedVotes[docClientId][summaryId]) {
            detailedVotes[docClientId][summaryId] = {}
          }
          detailedVotes[docClientId][summaryId][userId] = {
            vote: voteValue,
            comment: userVotes[`${summaryId}-comment`] || '',
            timestamp: voteDoc.createTime
          }
        })
      }
      commit('SET_DETAILED_SUMMARY_VOTES', detailedVotes)
      return detailedVotes
    } catch (error) {
      console.error('Error fetching detailed votes:', error)
      throw error
    }
  },

  async fetchAggregatedSummaryVotes ({ commit }) {
    try {
      const aggregatedVotes = {}
      const summaryVotesSnapshot = await db.collectionGroup('summaryVotes').get()
      for (const voteDoc of summaryVotesSnapshot.docs) {
        const pathSegments = voteDoc.ref.path.split('/')
        const clientId = pathSegments[1]
        const userId = voteDoc.id
        const userVotes = voteDoc.data()
        if (!aggregatedVotes[clientId]) {
          aggregatedVotes[clientId] = {}
        }
        Object.entries(userVotes).forEach(([summaryId, voteValue]) => {
          if (summaryId.endsWith('-comment')) return
          if (!aggregatedVotes[clientId][summaryId]) {
            aggregatedVotes[clientId][summaryId] = {}
          }
          aggregatedVotes[clientId][summaryId][userId] = {
            vote: voteValue,
            comment: userVotes[`${summaryId}-comment`] || '',
            timestamp: voteDoc.createTime
          }
        })
      }
      commit('SET_AGGREGATED_SUMMARY_VOTES', aggregatedVotes)
      return aggregatedVotes
    } catch (error) {
      console.error('Error fetching aggregated votes:', error)
      throw error
    }
  },

  async updateAiSummaryVote ({ commit }, { clientId, summaryId, vote, userId, comment }) {
    try {
      const summariesRef = await db
        .collection('ai_summaries')
        .doc(clientId)
        .collection('model_report')
        .orderBy('timestamp', 'desc')
        .limit(1)
        .get()
      if (summariesRef.docs.length === 0) {
        throw new Error('No summaries found')
      }
      const parentDoc = summariesRef.docs[0].ref
      const votesCollection = parentDoc.collection('summaryVotes')
      const userVoteDoc = votesCollection.doc(userId)

      const updateData = {
        [summaryId]: vote
      }

      if (comment && comment.length > 0) {
        updateData[`${summaryId}-comment`] = comment
      } else {
        updateData[`${summaryId}-comment`] = firebase.firestore.FieldValue.delete()
      }

      await userVoteDoc.set(updateData, { merge: true })
    } catch (error) {
      console.error('Error updating vote:', error)
      throw error
    }
  },

  async fetchModelReportSummaries ({ commit }, { clientId, userId }) {
    try {
      const summariesRef = await db
        .collection('ai_summaries')
        .doc(clientId)
        .collection('model_report')
        .orderBy('timestamp', 'desc')
        .limit(1)
        .get()
      let summaries = {}
      if (summariesRef.docs.length > 0) {
        const parentDoc = summariesRef.docs[0].ref
        const summariesCollection = await parentDoc.collection('summaries').get()
        const userVotesDoc = await parentDoc.collection('summaryVotes').doc(userId).get()
        const userVotes = userVotesDoc.exists ? userVotesDoc.data() : {}

        summariesCollection.forEach(doc => {
          const summaryId = doc.id
          summaries[summaryId] = {
            title: doc.data().title,
            text: doc.data().text,
            vote: userVotes[summaryId] || null,
            voteComment: userVotes[`${summaryId}-comment`]
          }
        })
      }
      commit('SET_MODEL_REPORT_SUMMARIES', { clientId, summaries })
      return summaries
    } catch (error) {
      throw error
    }
  },

  async generateModelReportAiSummaries (context, payload) {
    try {
      const result = await functions.httpsCallable('ai-generateModelReportAiSummary')({
        modelData: payload.modelData,
        systemPrompt: payload.systemPrompt,
        customAiModel: 'gemini-2.0-flash'
      })
      return result.data.summary.text
    } catch (error) {
      throw error
    }
  },

  async generatePlanOptimization (context, payload) {
    try {
      const result = await functions.httpsCallable('ai-generatePlanOptimizer')({
        userRequirements: payload.userRequirements,
        planDetails: payload.planDetails,
        systemPrompt: payload.systemPrompt,
        customAiModel: 'gemini-2.0-flash'
      })
      return result.data.summary.text
    } catch (error) {
      throw error
    }
  },
  async saveAiSummaries ({ commit }, { clientId, summaries, userId }) {
    try {
      const parentDoc = await db
        .collection('ai_summaries')
        .doc(clientId)
        .collection('model_report')
        .add({
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          createdBy: userId
        })
      const batch = db.batch()
      Object.entries(summaries).forEach(([key, value]) => {
        const summaryDoc = parentDoc
          .collection('summaries')
          .doc(key)
        batch.set(summaryDoc, {
          text: value.text,
          title: value.title
        })
      })
      await batch.commit()
      return parentDoc.id
    } catch (error) {
      throw error
    }
  }
}

const mutations = {
  SET_MODEL_REPORT_SUMMARIES (state, { clientId, summaries }) {
    state.modelReportSummaries = {
      ...state.modelReportSummaries,
      [clientId]: summaries
    }
  },
  SET_AGGREGATED_SUMMARY_VOTES (state, aggregatedVotes) {
    state.aggregatedSummaryVotes = aggregatedVotes
  },
  SET_DETAILED_SUMMARY_VOTES (state, detailedVotes) {
    state.detailedSummaryVotes = detailedVotes
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
